/**
 * Plantilla de botonera ARMADA A MANO (CONTRATO admin ↔ botón nativo).
 *
 * La categoría guarda `inicioTemplate = 'manual'` + `plantillaBotonera = { version, pantallas }`.
 * Son las MISMAS 3 pantallas que el vecino ya conoce (ver `pantallas-armado`):
 *  - pantalla 1: la que se abre al entrar a la app;
 *  - pantalla 2: se llega con el botón "Más" de la pantalla 1;
 *  - pantalla 3: se llega con el "Más" de la pantalla 2 (o con un botón de función Menu).
 * Cada pantalla es una lista de piezas (bloques) que el nativo (`PlantillaManualTemplate.kt`)
 * dibuja de arriba hacia abajo. Una pantalla sin piezas no existe para el vecino.
 *
 * Los botones se eligen UNO POR UNO en cada pieza (`idsBotones` / `items`). Al guardar, cada botón
 * queda en el `idsBtns*` de la pantalla donde se usó (`sincronizarPantallas`), así el resto del
 * sistema sigue viendo el reparto real.
 *
 * Cada bloque es un objeto PLANO con campos opcionales según `tipo`: así el nativo lo decodifica
 * con una sola data class y un campo nuevo no rompe versiones viejas de la app.
 *
 * No aplica a clientes con template hecho a mano en la app (por bundle ID): ahí la botonera está
 * bloqueada, igual que con las genéricas.
 */
import {
  IBloquePlantilla,
  ImagenBloquePlantilla,
  IPantallaPlantilla,
  IPlantillaBotonera,
  ItemFilaEspecial,
  TipoBloquePlantilla,
} from 'modelos/src';

/** Valor de `categoria.inicioTemplate` para la plantilla manual. */
export const ID_PLANTILLA_MANUAL = 'manual';

export const VERSION_PLANTILLA = 2;

export const CANTIDAD_PANTALLAS = 3;

export const PANTALLAS: { titulo: string; sub: string }[] = [
  { titulo: 'Pantalla 1', sub: 'La que se abre al entrar' },
  { titulo: 'Pantalla 2', sub: 'Se llega con el botón Más' },
  { titulo: 'Pantalla 3', sub: 'Con el Más de la pantalla 2' },
];

export const TIPOS_BLOQUE: {
  tipo: TipoBloquePlantilla;
  label: string;
  icono: string;
  descripcion: string;
}[] = [
  {
    tipo: 'imagen',
    label: 'Imagen',
    icono: 'image',
    descripcion:
      'Logo o banner del cliente (imagen "arriba" o "abajo"), con o sin franja de color detrás.',
  },
  {
    tipo: 'botones',
    label: 'Botones',
    icono: 'apps',
    descripcion: 'Los botones que elijas, en 1 a 3 columnas.',
  },
  {
    tipo: 'fila',
    label: 'Fila de acciones',
    icono: 'view_week',
    descripcion: 'Reflector, sirena, Más, Atrás o botones, uno al lado del otro.',
  },
  {
    tipo: 'texto',
    label: 'Texto',
    icono: 'title',
    descripcion: 'Un título o una leyenda.',
  },
  {
    tipo: 'espacio',
    label: 'Espacio',
    icono: 'height',
    descripcion: 'Separación vacía.',
  },
];

/** Valor de `colorFondo` de una imagen que usa el color del tema del cliente. */
export const FONDO_TEMA = 'tema';

export const IMAGENES_BLOQUE: { id: ImagenBloquePlantilla; label: string }[] = [
  { id: 'arriba', label: 'Arriba (logo / banner superior)' },
  { id: 'abajo', label: 'Abajo (banner inferior)' },
];

export const ITEMS_ESPECIALES: {
  id: ItemFilaEspecial;
  label: string;
  nota: string;
}[] = [
  { id: 'reflector', label: 'Reflector', nota: 'Solo si el cliente tiene sirenas.' },
  { id: 'sirena', label: 'Sirena', nota: 'Solo si el cliente tiene sirenas.' },
  { id: 'mas', label: 'Más', nota: 'Va a la pantalla siguiente, si tiene piezas.' },
  { id: 'atras', label: 'Atrás', nota: 'Vuelve a la pantalla anterior.' },
];

const ESPECIALES = new Set<string>(ITEMS_ESPECIALES.map((i) => i.id));

/** ¿El ítem de una fila es una pieza fija (reflector/sirena/más/atrás) o un botón? */
export function esItemEspecial(item: string): item is ItemFilaEspecial {
  return ESPECIALES.has(item);
}

/** Bloque nuevo con valores razonables para cada tipo. */
export function bloqueNuevo(tipo: TipoBloquePlantilla): IBloquePlantilla {
  switch (tipo) {
    case 'imagen':
      return { tipo, imagen: 'arriba', alto: 120, ajuste: 'contener' };
    case 'botones':
      return { tipo, idsBotones: [], columnas: 1, expandir: true };
    case 'fila':
      return { tipo, alto: 84, items: [] };
    case 'texto':
      return { tipo, texto: '', tamano: 18, negrita: true };
    case 'espacio':
      return { tipo, alto: 16 };
  }
}

/**
 * Plantilla de arranque a partir del reparto actual de la categoría, para no empezar de cero y
 * que se vea igual que la botonera `banner`: pantalla 1 con banner + botones + fila + footer;
 * pantallas 2 y 3 con sus botones + Atrás / Más.
 */
export function plantillaInicial(
  idsPantalla1: string[],
  idsPantalla2: string[],
  idsPantalla3: string[],
): IPlantillaBotonera {
  const hay2 = idsPantalla2.length > 0;
  const hay3 = idsPantalla3.length > 0;
  const pantallas: IPantallaPlantilla[] = [
    {
      bloques: [
        { tipo: 'imagen', imagen: 'arriba', alto: 180, ajuste: 'ancho' },
        { tipo: 'botones', idsBotones: [...idsPantalla1], columnas: 1, expandir: true },
        {
          tipo: 'fila',
          alto: 84,
          items: ['reflector', ...(hay2 ? ['mas'] : []), 'sirena'],
        },
        { tipo: 'imagen', imagen: 'abajo', alto: 60, ajuste: 'contener' },
      ],
    },
    {
      bloques: hay2
        ? [
            { tipo: 'imagen', imagen: 'arriba', alto: 96, ajuste: 'contener' },
            { tipo: 'botones', idsBotones: [...idsPantalla2], columnas: 1, expandir: true },
            { tipo: 'fila', alto: 84, items: ['atras', ...(hay3 ? ['mas'] : [])] },
          ]
        : [],
    },
    {
      bloques: hay3
        ? [
            { tipo: 'imagen', imagen: 'arriba', alto: 96, ajuste: 'contener' },
            { tipo: 'botones', idsBotones: [...idsPantalla3], columnas: 1, expandir: true },
            { tipo: 'fila', alto: 84, items: ['atras'] },
          ]
        : [],
    },
  ];
  return { version: VERSION_PLANTILLA, pantallas };
}

/** Las 3 pantallas, siempre presentes (una plantilla vieja o incompleta trae menos). */
export function pantallasDe(p?: IPlantillaBotonera | null): IPantallaPlantilla[] {
  return Array.from(
    { length: CANTIDAD_PANTALLAS },
    (_, i) => p?.pantallas?.[i] ?? { bloques: [] },
  );
}

/** Ids de botón que usa una pantalla (piezas Botones + botones de las filas), en orden y sin repetir. */
export function botonesDePantalla(pantalla?: IPantallaPlantilla | null): string[] {
  const out: string[] = [];
  for (const b of pantalla?.bloques ?? []) {
    const ids =
      b.tipo === 'botones'
        ? (b.idsBotones ?? [])
        : b.tipo === 'fila'
          ? (b.items ?? []).filter((i) => !esItemEspecial(i))
          : [];
    for (const id of ids) if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Cambia (o borra, con `null`) un id de botón en todas las piezas. */
export function reemplazarBoton(
  p: IPlantillaBotonera | null | undefined,
  de: string,
  a: string | null,
): IPlantillaBotonera | null {
  if (!p) return p ?? null;
  const cambiar = (ids?: string[]): string[] | undefined =>
    ids?.flatMap((id) => (id === de ? (a ? [a] : []) : [id]));
  return {
    ...p,
    pantallas: (p.pantallas ?? []).map((pa) => ({
      ...pa,
      bloques: (pa.bloques ?? []).map((b) => ({
        ...b,
        idsBotones: cambiar(b.idsBotones),
        items: cambiar(b.items),
      })),
    })),
  };
}

/**
 * Deja el reparto de botones de la categoría (`idsBtns*`) igual a lo que muestra la plantilla:
 * cada botón queda en la PRIMERA pantalla donde aparece, en el orden de las piezas. Los que no
 * están en ninguna pieza se quedan donde estaban (siguen en la categoría, pero no se ven).
 * Muta los arrays recibidos (son los de `CategoriaEditable`, compartidos por referencia).
 */
export function sincronizarPantallas(
  p: IPlantillaBotonera | null | undefined,
  idsPantallas: [string[], string[], string[]],
): void {
  const pantallas = pantallasDe(p);
  const usados = new Set<string>();
  const porPantalla = pantallas.map((pa) => {
    const ids = botonesDePantalla(pa).filter((id) => !usados.has(id));
    ids.forEach((id) => usados.add(id));
    return ids;
  });
  idsPantallas.forEach((arr, i) => {
    const resto = arr.filter((id) => !usados.has(id));
    arr.splice(0, arr.length, ...porPantalla[i], ...resto);
  });
}

/**
 * Normaliza lo que se guarda: solo los campos que usa cada tipo (el editor puede dejar restos),
 * números acotados a lo que el nativo sabe dibujar, y cada id de botón pasado por `resolverId`
 * (al guardar, `tmp-btn-N` → `_id` real; los que no resuelve se descartan).
 */
export function limpiarPlantilla(
  p: IPlantillaBotonera | null | undefined,
  resolverId: (id: string) => string | undefined = (id) => id,
): IPlantillaBotonera {
  const acotar = (n: unknown, min: number, max: number): number | undefined => {
    const v = Number(n);
    if (n === null || n === undefined || n === '' || !Number.isFinite(v)) {
      return undefined;
    }
    return Math.min(max, Math.max(min, Math.round(v)));
  };
  const ids = (lista?: string[]): string[] =>
    (lista ?? [])
      .map((id) => (esItemEspecial(id) ? id : resolverId(id)))
      .filter((id): id is string => !!id);
  const bloque = (b: IBloquePlantilla): IBloquePlantilla => {
    switch (b.tipo) {
      case 'imagen':
        return {
          tipo: b.tipo,
          imagen: b.imagen === 'abajo' ? 'abajo' : 'arriba',
          alto: acotar(b.alto, 24, 400) ?? 120,
          ajuste: b.ajuste === 'ancho' ? 'ancho' : 'contener',
          colorFondo:
            b.colorFondo === FONDO_TEMA || /^#[0-9a-f]{6}$/i.test(b.colorFondo ?? '')
              ? b.colorFondo
              : undefined,
        };
      case 'botones':
        return {
          tipo: b.tipo,
          idsBotones: ids(b.idsBotones),
          columnas: acotar(b.columnas, 1, 3) ?? 1,
          alto: acotar(b.alto, 40, 360),
          expandir: !!b.expandir,
        };
      case 'fila':
        return {
          tipo: b.tipo,
          alto: acotar(b.alto, 40, 160) ?? 84,
          items: ids(b.items),
        };
      case 'texto':
        return {
          tipo: b.tipo,
          texto: b.texto ?? '',
          tamano: acotar(b.tamano, 10, 40) ?? 18,
          color: b.color || undefined,
          negrita: !!b.negrita,
        };
      default:
        return { tipo: 'espacio', alto: acotar(b.alto, 4, 200) ?? 16 };
    }
  };
  return {
    version: VERSION_PLANTILLA,
    pantallas: pantallasDe(p).map((pa) => ({
      bloques: (pa.bloques ?? []).map(bloque),
    })),
  };
}
